import { app, functions } from "services/firebase"
import { getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail, GoogleAuthProvider, signInWithPopup, updatePassword, EmailAuthProvider, reauthenticateWithCredential, getAdditionalUserInfo } from "firebase/auth"
import { useCallback, useEffect, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { AuthenticationStatus, FormStatus, AccountPrivateInfoWrite, AccountPublicWrite } from "types"
import { logger } from "utils/secureLogger"
import { trackDelete, trackEdit, trackLogin, trackLogout, trackSignUp } from "services/analytics"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { setUserDoc, deleteUserAvatar, setUserAvatar, getUserPublicDoc, getUserPrivateDoc, deleteAccountDoc } from "services/firebaseApi"
import { httpsCallable } from "firebase/functions"
import { FirebaseError } from "firebase/app"
import { useAuthContext } from "../contexts/AuthContext"

const auth = getAuth(app)

const useAuth = () => {
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    
    // Get auth state from context
    const {
        user,
        tokenIsValid,
        tokenValidationStatus,
        authenticationMethod,
        setTokenValidationStatus,
    } = useAuthContext()

    // Query for public account data
    const {
        data: accountPublicData,
        isLoading: isLoadingAccountPublicData,
        error: accountPublicDataError,
        refetch: refetchAccountPublicData
    } = useQuery({
        queryKey: ['accountPublicData'],
        queryFn: () => getUserPublicDoc(),
        enabled: !!tokenIsValid,
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
    })

    // Query for private account data
    const {
        data: accountPrivateData,
        isLoading: isLoadingAccountPrivateData,
        error: accountPrivateDataError,
        refetch: refetchAccountPrivateData
    } = useQuery({
        queryKey: ['accountPrivateData'],
        queryFn: () => getUserPrivateDoc(),
        enabled: !!tokenIsValid,
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
    })

    // Sign up mutation
    const signUpMutation = useMutation({
        mutationFn: async ({ email, password }: { email: string, password: string }) => {
            await createUserWithEmailAndPassword(auth, email, password)
            logger.auth('User signed up', undefined, 'SignUp')
        },
        onError: (error: FirebaseError) => {
            logger.error('Sign up failed', error.code, 'Auth')
        },
        onSuccess: () => {
            trackSignUp('password')
        }
    })

    // Login mutation
    const loginMutation = useMutation({
        mutationFn: async ({ email, password }: { email: string, password: string }) => {
            await signInWithEmailAndPassword(auth, email, password)
            logger.auth('User logged in', undefined, 'Login')
        },
        onError: (error: FirebaseError) => {
            logger.error('Login failed', error.code, 'Auth')
        },
        onSuccess: () => {
            trackLogin('password')
        }
    })

    // Google sign in mutation
    const googleSignInMutation = useMutation({
        mutationFn: async () => {
            const provider = new GoogleAuthProvider()
            const result = await signInWithPopup(auth, provider)
            const user = result.user
            logger.auth('User signed in', user?.uid)
            const additional = getAdditionalUserInfo(result)
            if (additional?.isNewUser) {
                trackSignUp('google')
            } else {
                trackLogin('google')
            }
        },
        onError: (error: FirebaseError) => {
            logger.error('Google sign in failed', error.code, 'Auth')
        }
    })

    // Logout mutation
    const logoutMutation = useMutation({
        mutationFn: async () => {
            await auth.signOut()
        },
        onSuccess: () => {
            trackLogout()
            queryClient.clear() // Clear all cached data on logout
        },
        onError: (error: FirebaseError) => {
            logger.error('Logout failed', error.code, 'Auth')
        }
    })

    // Recover password mutation
    const recoverPasswordMutation = useMutation({
        mutationFn: async ({ email }: { email: string }) => {
            await sendPasswordResetEmail(auth, email)
            logger.info('Password reset email sent')
        },
        onError: (error: FirebaseError) => {
            logger.error('Password reset email failed', error.code, 'Auth')
        }
    })

    // Change password mutation
    const changePasswordMutation = useMutation({
        mutationFn: async ({ currentPassword, newPassword }: { currentPassword?: string, newPassword: string }) => {
            const user = auth.currentUser;
            if (!user) throw new Error('USER_NOT_SIGNED_IN');
            
            // Only allow password change if the user signed in with password
            if (!user.providerData.some(p => p.providerId === 'password')) {
                throw new Error('PROVIDER_NOT_PASSWORD');
            }

            // If current password is provided, re-authenticate first
            if (currentPassword && user.email) {
                const credential = EmailAuthProvider.credential(user.email, currentPassword);
                await reauthenticateWithCredential(user, credential);
            }

            await updatePassword(user, newPassword);
            logger.info('Password changed successfully');
        },
        onError: (error: FirebaseError) => {
            logger.error('Change password failed', error.code || error.message, 'Auth')
        }
    })

    // Update account data mutation
    const updateAccountDataMutation = useMutation({
        mutationFn: async ({ publicData, privateData }: { publicData: AccountPublicWrite, privateData: AccountPrivateInfoWrite }) => {
            logger.api('Updating user document', { accountData: logger.sanitizeData(publicData) })
            await setUserDoc(publicData, privateData)
        },
        onSuccess: () => {
            trackEdit('account')
            // Invalidate both public and private data queries
            queryClient.invalidateQueries({ queryKey: ['accountPublicData'] })
            queryClient.invalidateQueries({ queryKey: ['accountPrivateData'] })
            // Check account completion after successful update
            checkAccountCompletion()
        },
        onError: (error: FirebaseError) => {
            logger.error('Update account data failed', error.code, 'Auth')
        }
    })

    // Update account general data mutation (without avatar)
    const updateAccountGeneralDataMutation = useMutation({
        mutationFn: async ({ publicData, privateData }: { publicData: AccountPublicWrite, privateData: AccountPrivateInfoWrite }) => {
            logger.api('Updating user general data', { accountData: logger.sanitizeData(publicData) })
            await setUserDoc(publicData, privateData)
        },
        onSuccess: () => {
            trackEdit('account')
            // Invalidate both public and private data queries
            queryClient.invalidateQueries({ queryKey: ['accountPublicData'] })
            queryClient.invalidateQueries({ queryKey: ['accountPrivateData'] })
            // Check account completion after successful update
            checkAccountCompletion()
        },
        onError: (error: FirebaseError) => {
            logger.error('Update account general data failed', error.code, 'Auth')
        }
    })

    // Update account avatar mutation
    const updateAccountAvatarMutation = useMutation({
        mutationFn: async (accountPublicDocData: AccountPublicWrite) => {
            logger.api('Updating user avatar')
            await setUserAvatar(accountPublicDocData)
        },
        onSuccess: () => {
            // Invalidate public data query (avatar is stored in public doc)
            queryClient.invalidateQueries({ queryKey: ['accountPublicData'] })
        },
        onError: (error: FirebaseError) => {
            logger.error('Update account avatar failed', error.code, 'Auth')
        }
    })

    // Claim account with token mutation
    const claimAccountWithTokenMutation = useMutation({
        mutationFn: async ({ token, password }: { token: string, password: string }) => {
            const claimAccountFunction = httpsCallable(functions, 'claimAccountWithToken');
            await claimAccountFunction({ token, password });
        },
        onSuccess: () => {
            trackSignUp('claim')
        },
        onError: (error: FirebaseError) => {
            logger.error('Claim account failed', error.code, 'Auth')
        }
    })

    // Delete account mutation
    const deleteAccountMutation = useMutation({
        mutationFn: async ({ email }: { email: string }) => {
            logger.api('Deleting user account')
            await deleteAccountDoc({ email })
            await auth.signOut()
        },
        onSuccess: () => {
            trackDelete('account')
            queryClient.clear()
        },
        onError: (error: FirebaseError) => {
            logger.error('Delete account failed', error.code, 'Auth')
        }
    })

    // Delete avatar mutation
    const deleteAvatarMutation = useMutation({
        mutationFn: async () => {
            logger.api('Deleting user avatar')
            await deleteUserAvatar()
        },
        onSuccess: () => {
            // Invalidate public data query (avatar is stored in public doc)
            queryClient.invalidateQueries({ queryKey: ['accountPublicData'] })
        },
        onError: (error: FirebaseError) => {
            logger.error('Delete avatar failed', error.code, 'Auth')
        }
    })

    // Refetch both public and private data
    const refetchAccountData = useCallback(async () => {
        await Promise.all([
            refetchAccountPublicData(),
            refetchAccountPrivateData()
        ]);
    }, [refetchAccountPublicData, refetchAccountPrivateData]);

    // Check account completion logic
    const checkAccountCompletion = useCallback(() => {
        if (!accountPublicData || !accountPublicData.other) {
            console.log("No account data");
            return;
        }
        return accountPublicData.other?.isComplete;
    }, [accountPublicData]);

    // Check account completion when account data changes
    useEffect(() => {
        // While the account data has value isComplete == undefined, refetch the use document
        // until isComplete is no longer undefined. This works since the backend sets isComplete
        // to false during creation of user document.
        //
        // Note that this method is used as we're using basic sign up /login methods
        // provided by the library, and not custom onCall functions.
        if (tokenIsValid && (!accountPublicData || !accountPrivateData)) {
            refetchAccountData();
        }
        if (tokenIsValid && accountPublicData) {
            checkAccountCompletion();
        }
    }, [tokenIsValid, accountPublicData, checkAccountCompletion, refetchAccountData]);

    const enforceAccountCompletion = useCallback((fallbackURL: string) => {
        // If account is not complete or no check have been done.
        if (!accountPublicData || accountPublicData.other?.isComplete) return
        // Thus it's false.
        logger.info('Account incomplete, redirecting', { fallbackURL }, 'AuthRedirect')
        console.log(accountPublicData)
        navigate(fallbackURL)
    }, [accountPublicData, navigate])

    /**
     * Get User Authentication Method
     * 
     * This function returns the sign-in method used by the user,
     * as some functions are only available for certain methods.
     */
    const getAuthenticationMethod = useMemo(() => {
        if (authenticationMethod === null) return null
        return authenticationMethod
    }, [authenticationMethod])

    /**
     * Enforce Restriction.
     * 
     * Either navigates the user if a fallback URL is provided,
     * or returns a boolean to be interpreted.
     * 
     * @param restrictedTo
     * @param fallbackURL 
     * @returns boolean
     */
    const enforceRestriction = useCallback((restrictedTo: string, fallbackURL?: string) => {
        if (tokenValidationStatus === FormStatus.LOADING) return false
        let enforce = false
        if (
            (restrictedTo === AuthenticationStatus.online && tokenIsValid === false) ||
            (restrictedTo === AuthenticationStatus.offline && tokenIsValid === true)
        ) enforce = true
        if (fallbackURL === undefined) {
            return enforce
        } else if (enforce) {
            logger.info('Authentication restriction triggered', { restrictedTo, fallbackURL }, 'AuthRedirect')
            navigate(fallbackURL)
        }
    }, [tokenValidationStatus, tokenIsValid, navigate])

    /**
     * Return user id (uid) if the user is authenticated.
     */
    const getUserId = useCallback(() => {
        if (tokenIsValid && user) {
            return user.uid
        }
        return null
    }, [tokenIsValid, user])

    return {
        // State
        tokenValidationStatus,
        tokenIsValid,
        accountPublicData,
        accountPrivateData,
        user,
        getAuthenticationMethod,
        getUserId,
        
        // Navigation helpers
        enforceAccountCompletion,
        enforceRestriction,
        
        // Queries
        isLoadingAccountPublicData,
        accountPublicDataError,
        isLoadingAccountPrivateData,
        accountPrivateDataError,
        refetchAccountData,
        
        // Mutations
        signUp: signUpMutation.mutateAsync,
        isSigningUp: signUpMutation.isPending,
        signUpError: signUpMutation.error,
        
        login: loginMutation.mutateAsync,
        isLoggingIn: loginMutation.isPending,
        loginError: loginMutation.error,
        
        googleSignIn: googleSignInMutation.mutateAsync,
        isGoogleSigningIn: googleSignInMutation.isPending,
        googleSignInError: googleSignInMutation.error,
        
        logout: logoutMutation.mutateAsync,
        isLoggingOut: logoutMutation.isPending,
        logoutError: logoutMutation.error,
        
        recoverPassword: recoverPasswordMutation.mutateAsync,
        isRecoveringPassword: recoverPasswordMutation.isPending,
        recoverPasswordError: recoverPasswordMutation.error,
        
        changePassword: changePasswordMutation.mutateAsync,
        isChangingPassword: changePasswordMutation.isPending,
        changePasswordError: changePasswordMutation.error,
        
        updateAccountData: updateAccountDataMutation.mutateAsync,
        isUpdatingAccountData: updateAccountDataMutation.isPending,
        updateAccountDataError: updateAccountDataMutation.error,
        
        updateAccountGeneralData: updateAccountGeneralDataMutation.mutateAsync,
        isUpdatingAccountGeneralData: updateAccountGeneralDataMutation.isPending,
        updateAccountGeneralDataError: updateAccountGeneralDataMutation.error,
        
        updateAccountAvatar: updateAccountAvatarMutation.mutateAsync,
        isUpdatingAccountAvatar: updateAccountAvatarMutation.isPending,
        updateAccountAvatarError: updateAccountAvatarMutation.error,
        
        claimAccountWithToken: claimAccountWithTokenMutation.mutateAsync,
        isClaimingAccount: claimAccountWithTokenMutation.isPending,
        claimAccountError: claimAccountWithTokenMutation.error,
        
        deleteAvatar: deleteAvatarMutation.mutateAsync,
        isDeletingAvatar: deleteAvatarMutation.isPending,
        deleteAvatarError: deleteAvatarMutation.error,

        deleteAccount: deleteAccountMutation.mutateAsync,
        isDeletingAccount: deleteAccountMutation.isPending,
        deleteAccountError: deleteAccountMutation.error,
    }
    
}

export default useAuth
